/**
 * Presentational helpers shared across the Workspace views (PR4-fidelity).
 * Pure — no React. Maps the prototype's avatar / due / date conventions onto
 * the app's tokens.
 */

import { ApiError, type WorkItemRow } from '@/lib/api';
import { contactSentence, type SupportContact } from '@/lib/errorCopy';
import { practiceTodayIso } from '@/lib/practiceDate';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM-DD' (or ISO) → 'Mon D'; '' for blank/invalid. */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '';
  const p = value.slice(0, 10).split('-').map(Number);
  const m = p[1];
  const d = p[2];
  if (!m || !d || m < 1 || m > 12) return '';
  return `${MONTHS[m - 1]} ${d}`;
}

/**
 * today as 'YYYY-MM-DD' in the PRACTICE's calendar day (America/New_York),
 * the anchor for every overdue/agenda "today" (founder ruling 2026-07-17:
 * practice-local day, not the viewer's device day and not UTC). It is
 * practiceTodayIso, the one helper My Clients' date line and the home read
 * use, so the overdue count and the date beside it never disagree. MIRROR:
 * api/_shared/businessDays.ts practiceTodayIso, the server's overdue flag
 * (workNotifications) and SLA seeding anchor.
 */
export function todayStr(): string {
  return practiceTodayIso();
}

/** A missed deadline on a card that is still open: not done and not closed. */
export function isOverdue(item: { due_date: string | null; status: WorkItemRow['status']; closed_at: string | null }): boolean {
  return item.due_date !== null && item.status !== 'done' && item.closed_at == null && item.due_date < todayStr();
}

/** The next-action day has passed on a card that is still open (not done,
 *  not closed). A nudge register, kept apart from isOverdue: a missed chase
 *  day is not a missed deadline. */
export function nextActionPassed(item: { next_action_date: string | null; status: WorkItemRow['status']; closed_at: string | null }): boolean {
  return item.next_action_date !== null && item.status !== 'done' && item.closed_at === null && item.next_action_date < todayStr();
}

/**
 * The colour for a stage/group `done/total` progress count. Terracotta means
 * LATE, not merely incomplete: complete → sage; otherwise overdue → pepper
 * (terracotta), else in-progress → warm-700 (the legible gold-bronze the card
 * due-date uses — NOT the bright warm-400). `overdue` is the EFFECTIVE overdue
 * for this count (the caller scopes it to the case's current stage so an overdue
 * case doesn't turn every stage red). Precedence: complete > overdue > in-progress.
 *
 * AA (drawer design pass 2026-07-25): the overdue arm is `pepper-deep`, not
 * `pepper`. This count renders at 11.5px semibold, so it needs the 4.5:1
 * floor — plain pepper measures 4.38:1 on cream and 4.18:1 on the current
 * stage's sage-50 panel, and misses it on both. pepper-deep is index.css's
 * documented small-text danger tone and clears at 5.88:1 / 5.60:1. Same
 * meaning, same family, legible. (Shared with the board card's chip, which
 * gains the same fix.)
 */
export function stageCountClass(done: number, total: number, overdue: boolean): string {
  if (done >= total) return 'text-sage-400';
  return overdue ? 'text-pepper-deep' : 'text-warm-700';
}

/** Up-to-two-initial monogram from a display name; '?' when unknown. Trailing
 *  credentials after a comma ("Maria Curry, BSN RN") are dropped first. */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const base = name.split(',')[0]!.trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** A card's display name: title, else prospect_name, else a placeholder. */
export function cardName(item: { title: string; prospect_name: string | null }): string {
  return item.title || item.prospect_name || 'Untitled';
}

/**
 * Slice 3a/3b: turn a failed move/assign into an operator-facing reason. The
 * 409/422 envelope carries a machine `error` CODE (response.ts) and, for the
 * required-checklist STOP, the specific unfinished item labels. Switch on the
 * code so each STOP surfaces its OWN reason — critically, an unset operating
 * company ("client_company_unconfigured") must NOT read as "finish the
 * checklist". That collapse is what stranded every intake at Client Setup: the
 * code already reaches the client, this just stops masking it. NEVER the word
 * "gate" (Amanda r1). e.g. "Finish Paperwork Received, Billing into QBO first."
 */
const MOVE_ERROR_MESSAGES: Record<string, string> = {
  client_company_unconfigured: "Client Setup isn't set up yet. Ask {contact_admin} to finish the setup, then move the card.",
  client_not_set_up: 'Finish Client Setup before assigning.',
  no_assignment_target: 'Pick a care manager or at least one unit first.',
  not_a_care_manager: "That person isn't an active care manager. Pick someone else.",
  work_item_closed: 'This card is closed. Reopen it to make changes.',
  // PR3: with the process_item move arm live, this code fires only for TASKS
  // (which have no stages at all) — the copy says so instead of the old
  // "not an intake" (which would have gaslit a process-board user).
  work_item_not_intake: "Tasks stay where they are. Mark the task done when it's finished.",
  intake_status_not_editable: "This intake is closed, so it can't be edited. Reopen the card if it needs a change.",
  // PR3: a process_item move/create aimed at a stage that isn't on the card's
  // board (or a board that's gone) — 404 process_not_available.
  process_not_available: 'That stage is no longer on this board. Reload the page and move the card again.',
};

export function moveBlockedMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError) {
    const body = (err.body ?? null) as { error?: string; incomplete_gate_labels?: string[]; incomplete_labels?: string[] } | null;
    const code = body?.error;
    // The required-checklist STOP names the specific unfinished item(s) — never "gate".
    if (code === 'gate_not_met') {
      const labels = body?.incomplete_gate_labels;
      if (labels && labels.length > 0) return `Finish ${labels.join(', ')} first.`;
      return 'Finish the required checklist items first.';
    }
    // The client-creation STOP (founder rule): no client record before
    // paperwork is back. Names the unfinished item(s); never "gate".
    if (code === 'paperwork_not_received') {
      const labels = body?.incomplete_labels;
      if (labels && labels.length > 0) return `The paperwork has to come back first. Finish ${labels.join(', ')}, then move the card.`;
      return 'The paperwork has to come back before Client Setup. Tick it off on the card, then move it.';
    }
    const mapped = code ? MOVE_ERROR_MESSAGES[code] : undefined;
    if (mapped) return contactSentence(mapped, contact);
  }
  return contactSentence("The card didn't move. Try again, and if it happens again, tell {contact_system}.", contact);
}

/**
 * Slice 3b-2: the unit's card dot color. Prefers the tenant's configured
 * units.color (hex); when unset, a deterministic tenant-NEUTRAL palette slot keyed
 * by a stable hash of the unit id — so un-themed tenants still get distinct dots,
 * with no unit name/hex ever hardcoded in product code (§3).
 */
// UX sweep 2026-07-10: the fallback slots are brand-family values
// (sage-400, warm-400, pepper, affirm-deep, warm-500, sage-600) so an
// un-themed tenant's dots stay on the house palette; a configured
// units.color still wins.
export const UNIT_DOT_PALETTE = ['#52796F', '#C4A265', '#C1512D', '#2E6E5B', '#A8874F', '#2F4D47'];
export function unitDotColor(unitId: string | null, unitColor: string | null): string | null {
  if (unitColor) return unitColor;
  if (!unitId) return null;
  return dotPaletteSlot(unitId);
}

/**
 * Field pack v1: the card dot for a process board's category value (e.g. a
 * hiring board's roles). Same deterministic tenant-neutral palette hash as
 * the unit fallback, keyed by the VALUE string — equal values share a
 * color, and no value/hex is ever hardcoded (§3). No configured-hex tier
 * (yet); if operators want pinned colors, that's a config follow-on.
 */
export function categoryDotColor(category: string | null): string | null {
  if (!category) return null;
  return dotPaletteSlot(category.toLowerCase());
}

function dotPaletteSlot(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return UNIT_DOT_PALETTE[h % UNIT_DOT_PALETTE.length]!;
}

/** Avatar classes — sage fill when assigned, dashed cream when not. `size` 'md'
 *  is the owner/default (w-6); 'sm' (w-5) is a collaborator in the card stack. */
export function avatarClasses(assigned: boolean, size: 'md' | 'sm' = 'md'): string {
  const dim = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-6 h-6 text-[10.5px]';
  return assigned
    ? `inline-flex items-center justify-center ${dim} rounded-pill bg-sage-400 text-white font-semibold flex-shrink-0`
    : `inline-flex items-center justify-center ${dim} rounded-pill bg-cream text-sage-400 border border-dashed border-sage-300 font-semibold flex-shrink-0`;
}

/**
 * Shared assignment (0108): split a card's collaborators into the avatars that
 * render and the overflow count. The card shows the owner + up to `cap`
 * collaborators, then a "+N" pill. Pure — unit-tested; the card never re-derives
 * the cap inline.
 */
export function collaboratorStack<T>(collaborators: T[], cap = 2): { visible: T[]; overflow: number } {
  return { visible: collaborators.slice(0, cap), overflow: Math.max(0, collaborators.length - cap) };
}
